import { useState, useEffect, useRef } from "react";
import type { WorkItem } from "@yagni/shared";

/**
 * Batch-resolves ticket identifiers found in message text.
 * Returns a map from identifier string to WorkItem.
 */
export function useWorkItemResolver(identifiers: string[]): {
  tickets: Map<string, WorkItem>;
  loading: boolean;
} {
  const [tickets, setWorkItems] = useState<Map<string, WorkItem>>(new Map());
  const [loading, setLoading] = useState(false);
  const prevKey = useRef("");

  useEffect(() => {
    if (identifiers.length === 0) {
      setWorkItems(new Map());
      return;
    }

    const key = [...identifiers].sort().join(",");
    if (key === prevKey.current) return;
    prevKey.current = key;

    setLoading(true);
    fetch(`/api/work-items/resolve?identifiers=${encodeURIComponent(identifiers.join(","))}`)
      .then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as WorkItem[];
          const map = new Map<string, WorkItem>();
          for (const t of data) {
            map.set(t.identifier, t);
          }
          setWorkItems(map);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [identifiers]);

  return { tickets, loading };
}

/**
 * Extract ticket identifier patterns from text.
 * Matches patterns like ACM-1, YAG-123, GLOB-42.
 * False positives (e.g. HTTP-200) are harmless — they simply won't resolve
 * via the API (which filters by customer_id) and render as muted badges.
 */
export function extractTicketIdentifiers(text: string): string[] {
  const matches = text.match(/\b[A-Z]{2,5}-\d+\b/g);
  return matches ? [...new Set(matches)] : [];
}
